import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from './components/auth'
import { ClientProtectedRoute } from './components/auth/client-protected-route'
import { MainLayout } from './components/layout'
import { ClientPortalLayout } from './components/layout/client-portal-layout'

// Lazy load all pages for code-splitting
const LoginPage = lazy(() => import('./pages/login-page').then(m => ({ default: m.LoginPage })))
const DashboardPage = lazy(() => import('./pages/dashboard-page').then(m => ({ default: m.DashboardPage })))
const AuthCallbackPage = lazy(() => import('./pages/auth-callback-page').then(m => ({ default: m.AuthCallbackPage })))
const ResetPasswordPage = lazy(() => import('./pages/reset-password-page').then(m => ({ default: m.ResetPasswordPage })))
const AcceptInvitationPage = lazy(() => import('./pages/accept-invitation').then(m => ({ default: m.AcceptInvitationPage })))
const ClientsPage = lazy(() => import('./pages/clients-page').then(m => ({ default: m.ClientsPage })))
const TaxpayersPage = lazy(() => import('./pages/taxpayers-page').then(m => ({ default: m.TaxpayersPage })))
const EngagementsPage = lazy(() => import('./pages/engagements-page').then(m => ({ default: m.EngagementsPage })))
const FilingsPage = lazy(() => import('./pages/filings-page').then(m => ({ default: m.FilingsPage })))
const TasksPage = lazy(() => import('./pages/tasks-page').then(m => ({ default: m.TasksPage })))
const ReportsPage = lazy(() => import('./pages/reports-page').then(m => ({ default: m.ReportsPage })))
const DocumentsPage = lazy(() => import('./pages/documents-page').then(m => ({ default: m.DocumentsPage })))
const StaffPage = lazy(() => import('./pages/staff-page').then(m => ({ default: m.StaffPage })))
const RiskPage = lazy(() => import('./pages/risk-page').then(m => ({ default: m.RiskPage })))
const SettingsPage = lazy(() => import('./pages/settings-page').then(m => ({ default: m.SettingsPage })))

// Portal pages
const PortalLoginPage = lazy(() => import('./pages/portal/portal-login-page').then(m => ({ default: m.PortalLoginPage })))
const PortalAcceptInvitationPage = lazy(() => import('./pages/portal/portal-accept-invitation-page').then(m => ({ default: m.PortalAcceptInvitationPage })))
const PortalDashboardPage = lazy(() => import('./pages/portal/portal-dashboard-page').then(m => ({ default: m.PortalDashboardPage })))
const PortalDocumentsPage = lazy(() => import('./pages/portal/portal-documents-page').then(m => ({ default: m.PortalDocumentsPage })))
const PortalFilingsPage = lazy(() => import('./pages/portal/portal-filings-page').then(m => ({ default: m.PortalFilingsPage })))
const PortalMessagesPage = lazy(() => import('./pages/portal/portal-messages-page').then(m => ({ default: m.PortalMessagesPage })))
const PortalInvoicesPage = lazy(() => import('./pages/portal/portal-invoices-page').then(m => ({ default: m.PortalInvoicesPage })))
const PortalAppointmentsPage = lazy(() => import('./pages/portal/portal-appointments-page').then(m => ({ default: m.PortalAppointmentsPage })))
const PortalSignPage = lazy(() => import('./pages/portal/portal-sign-page').then(m => ({ default: m.PortalSignPage })))
const PortalEngagementDetailPage = lazy(() => import('./pages/portal/portal-engagement-detail-page').then(m => ({ default: m.PortalEngagementDetailPage })))

/**
 * Main application component with routing.
 * Staff routes at /* and client portal routes at /portal/*
 */
function App() {
  return (
    <Suspense fallback={<div className="page-loading">Loading...</div>}>
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal/accept-invitation" element={<PortalAcceptInvitationPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/accept-invitation" element={<AcceptInvitationPage />} />

      {/* Staff protected routes — shared MainLayout persists across navigation */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/taxpayers" element={<TaxpayersPage />} />
        <Route path="/engagements" element={<EngagementsPage />} />
        <Route path="/filings" element={<FilingsPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/staff" element={<StaffPage />} />
        <Route path="/risk" element={<RiskPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Client Portal routes — shared ClientPortalLayout persists across navigation */}
      <Route element={<ClientProtectedRoute><ClientPortalLayout /></ClientProtectedRoute>}>
        <Route path="/portal" element={<PortalDashboardPage />} />
        <Route path="/portal/engagements/:engagementId" element={<PortalEngagementDetailPage />} />
        <Route path="/portal/documents" element={<PortalDocumentsPage />} />
        <Route path="/portal/filings" element={<PortalFilingsPage />} />
        <Route path="/portal/messages" element={<PortalMessagesPage />} />
        <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
        <Route path="/portal/appointments" element={<PortalAppointmentsPage />} />
        <Route path="/portal/sign/:engagementId" element={<PortalSignPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 fallback */}
      <Route
        path="*"
        element={
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <h1>404 - Page Not Found</h1>
            <p>The page you&apos;re looking for doesn&apos;t exist.</p>
            <a href="/dashboard">Go to Dashboard</a>
          </div>
        }
      />
      </Routes>
    </Suspense>
  )
}

export default App
